import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Table, TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { map, startWith, combineLatest, Subject } from 'rxjs';
import { RestrictionsService } from 'src/app/services/restrictions.service';
import { FpcWithRestrictions } from 'src/app/models/fpc-with-restrictions';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { RouterModule } from '@angular/router';

type Row = FpcWithRestrictions['restrictions'][number] & {
  fpcCode: string;
  fpcLabel: string;
};

type SortMeta = { field: string; order: 1 | -1 };

@Component({
  selector: 'app-fpcs-table',
  standalone: true,
  imports: [
    CommonModule, AsyncPipe, RouterModule,
    TableModule, RippleModule, IconFieldModule, InputIconModule, InputTextModule,
    FormsModule, ReactiveFormsModule, CardModule, ButtonModule
  ],
  templateUrl: './fpcs-table.component.html',
  styleUrls: ['./fpcs-table.component.scss']
})
export class FpcsTableComponent {
  @ViewChild('tbl') tbl!: Table;

  rowsPerPage = 12;

  private search$ = new Subject<string>();

  sortMeta: SortMeta[] = [{ field: 'fpcCode', order: 1 }];

  rows$ = combineLatest([
    this.restrictionsService.getAllFpcsWithRestrictions$(),
    this.search$.pipe(startWith(''))
  ]).pipe(
    map(([fpcs, q]) => {
      const query = (q ?? '').toLowerCase();
      const flat: Row[] = fpcs.flatMap(f =>
        (f.restrictions ?? []).map(r => ({
          ...r,
          fpcCode: f.fpcId,
          fpcLabel: this.buildFpcLabel(f.fpcId, f.name)
        }))
      );
      if (!query) return flat;
      return flat.filter(r =>
        r.fpcLabel.toLowerCase().includes(query) ||
        String(r.displayName ?? '').toLowerCase().includes(query) ||
        r.restrictionName.toLowerCase().includes(query) ||
        r.responsible.toLowerCase().includes(query)
      );
    })
  );

  constructor(private restrictionsService: RestrictionsService, private router: Router) {}

  goFpc(fpcId: string) {
    this.router.navigate(['fpcs', fpcId]);
  }

  buildFpcLabel(fpcId: string, name: string): string {
    return `${fpcId.replace(/^FPC/i, '')} - ${name}`;
  }

  sortWithinGroups(e: any) {
    const data = e.data as Row[];
    const field = e.field as keyof Row;
    const order = (e.order ?? 1) as 1 | -1;

    data.sort((a, b) => {
      const fpcOrder = field === 'fpcCode' ? order : 1;
      const g = a.fpcCode.localeCompare(b.fpcCode) * fpcOrder;
      if (g !== 0) return g;

      if (field !== 'fpcCode') {
        const av = (a as any)[field];
        const bv = (b as any)[field];
        return (av < bv ? -1 : av > bv ? 1 : 0) * order;
      }
      return 0;
    });
  }

  onSort(e: { field?: string; order?: 1 | -1 }) {
    const f = e.field ?? 'fpcCode';
    const o = (e.order ?? 1) as 1 | -1;

    this.sortMeta = [{ field: 'fpcCode', order: 1 }];
    if (f !== 'fpcCode') this.sortMeta.push({ field: f, order: o });

    this.tbl.sortMultiple();
  }

  onGlobalFilter(value: string) {
    this.search$.next(value ?? '');
  }
}
